import React, { Fragment, useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import ExtensionLoader from './ExtensionLoader'
import { ExtensionPointError } from './ExtensionError'
import { Spinner, csx } from '@vtex/admin-ui'
import { useCleanUpExtension } from './useCleanUpExtension'
import { logger } from '@/checkout-instore-legacy/Logger'
import safeStringify from 'fast-safe-stringify'

interface ExtensionPointProps {
  appName: string
  appProps: Record<string, unknown>
  onError: (err: ExtensionPointError) => void
  afterLoadHook: () => void
  cleanUpHook: () => void
}

const loadExtensionPoint = async (
  appName: string,
  appProps: Record<string, unknown>,
  onError: (err: ExtensionPointError) => void,
  onChangeLoading: (loading: boolean) => void,
  afterLoadHook: () => void
) => {
  const renderer = new ExtensionLoader()

  try {
    onChangeLoading(true)
    await renderer.load()

    const extensionPointElement = document.getElementById(appName)
    if (extensionPointElement) {
      afterLoadHook()

      await renderer.render(extensionPointElement, appName, appProps)
      onChangeLoading(false)
    }
  } catch (error) {
    console.error(error)

    logger.error({
      message: 'Failed to load Extension Point',
      workflowType: 'extension-point-load-error',
      error: error instanceof Error ? error : new Error(safeStringify(error)),
    })

    onChangeLoading(false)
    onError(error)
  }
}

export const ExtensionPoint = ({
  appName,
  appProps,
  onError: handleError,
  afterLoadHook,
  cleanUpHook,
}: ExtensionPointProps) => {
  useEffect(() => cleanUpHook, [cleanUpHook])
  const cleanUpExtensionHook = useCleanUpExtension()

  const [loading, setLoading] = useState(true)
  useEffect(() => {
    loadExtensionPoint(
      appName,
      { ...appProps, cleanUpHook: cleanUpExtensionHook },
      handleError,
      setLoading,
      afterLoadHook
    )
  }, [
    appName,
    appProps,
    cleanUpExtensionHook,
    handleError,
    setLoading,
    afterLoadHook,
  ])

  return (
    <Fragment>
      {loading && (
        <div
          className={csx({
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            height: '100%',
          })}
        >
          <Spinner className={csx({ marginBottom: '12px' })} />
          <FormattedMessage id="paymentApp.loading" />
        </div>
      )}
      <div
        data-cy="extension-point"
        className={csx({
          height: `${loading ? '0px' : '100%'}`,
        })}
        id={appName}
      />
    </Fragment>
  )
}
